import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/api";
import { LOGIN_HERO } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Building2 } from "lucide-react";

export default function Registro() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ empresa_nombre: "", nombre: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) return setError("La contraseña debe tener al menos 8 caracteres");
    setLoading(true);
    try {
      await register(form);
      navigate("/");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#001D33]">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={LOGIN_HERO} alt="Desarrollo inmobiliario" className="absolute inset-0 w-full h-full object-cover"
          style={{ animation: "kenburns 22s ease-in-out infinite alternate" }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#001D33] via-[#002B49]/50 to-transparent" />
        <div className="absolute bottom-0 left-0 p-12 text-white">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="text-xs tracking-[0.3em] uppercase text-[#00A3B5] font-bold mb-3">Tu empresa, tus proyectos</motion.p>
          <motion.h2 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}
            className="font-display text-4xl font-bold leading-tight max-w-md">
            Empieza a controlar<br />tu desarrollo hoy.
          </motion.h2>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
            className="mt-4 text-white/60 max-w-sm text-sm leading-relaxed">
            Registra tu empresa y gestiona todos tus desarrollos inmobiliarios en un solo lugar: trámites, coordinación, producto y financiamiento, con evidencia auditable.
          </motion.p>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-11 h-11 rounded-md bg-[#00A3B5] flex items-center justify-center">
              <Building2 className="text-white" size={22} />
            </div>
            <div>
              <p className="font-display font-bold text-white text-xl leading-none">Developware</p>
              <p className="text-white/50 text-xs mt-1">Gestión inmobiliaria integral</p>
            </div>
          </div>
          <h1 className="font-display text-3xl font-bold text-white mb-2">Registra tu empresa</h1>
          <p className="text-white/50 text-sm mb-8">Crea la cuenta de administrador de tu empresa. Después podrás agregar tu logo, portada, proyectos y usuarios.</p>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-white/70">Nombre de la empresa</Label>
              <Input data-testid="registro-empresa-input" required value={form.empresa_nombre} onChange={set("empresa_nombre")}
                placeholder="Constructora Ejemplo S.A. de C.V." className="bg-white/5 border-white/15 text-white placeholder:text-white/30 h-12" />
            </div>
            <div className="space-y-2">
              <Label className="text-white/70">Tu nombre</Label>
              <Input data-testid="registro-nombre-input" required value={form.nombre} onChange={set("nombre")}
                placeholder="Nombre del administrador" className="bg-white/5 border-white/15 text-white placeholder:text-white/30 h-12" />
            </div>
            <div className="space-y-2">
              <Label className="text-white/70">Correo electrónico</Label>
              <Input data-testid="registro-email-input" type="email" required value={form.email} onChange={set("email")}
                placeholder="admin@tuempresa.com" className="bg-white/5 border-white/15 text-white placeholder:text-white/30 h-12" />
            </div>
            <div className="space-y-2">
              <Label className="text-white/70">Contraseña (mínimo 8 caracteres)</Label>
              <Input data-testid="registro-password-input" type="password" required value={form.password} onChange={set("password")}
                placeholder="••••••••" className="bg-white/5 border-white/15 text-white placeholder:text-white/30 h-12" />
            </div>
            {error && <p data-testid="registro-error" className="text-red-400 text-sm">{error}</p>}
            <Button data-testid="registro-submit-button" type="submit" disabled={loading}
              className="w-full h-12 bg-[#00A3B5] hover:bg-[#008C9C] text-white font-bold text-base active:scale-[0.98] transition-transform">
              {loading ? "Creando cuenta..." : "Crear cuenta de empresa"}
            </Button>
          </form>
          <p className="mt-6 text-sm text-white/50 text-center">
            ¿Ya tienes cuenta?{" "}
            <Link data-testid="link-login" to="/login" className="text-[#00A3B5] hover:underline font-semibold">Inicia sesión</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
